import { useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation, useLang } from "@/lib/i18n";
import SiteFooter from "@/components/SiteFooter";
import { usePostHog } from "@posthog/react";

interface FooterCTAProps {
  variant?: "aiTeams" | "oneToOne" | "web3audit";
}

const FooterCTA = ({ variant = "aiTeams" }: FooterCTAProps) => {
  const t = useTranslation();
  const { lang } = useLang();
  const isPL = lang === "pl";
  const prefersReduced = useReducedMotion();
  const posthog = usePostHog();

  const footer =
    variant === "oneToOne"
      ? t.oneToOne.footer
      : variant === "web3audit"
      ? t.web3audit.footer
      : t.aiTeams.footer;

  const ctaIsAnchor = true;
  const isAmber = variant === "oneToOne";

  const isFinePointer = typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;
  const ctaContainerRef = useRef<HTMLDivElement>(null);

  const handleCtaMove = useCallback((e: React.MouseEvent) => {
    if (prefersReduced || !isFinePointer) return;
    const target = e.currentTarget.firstElementChild as HTMLElement;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = Math.max(-10, Math.min(10, (e.clientX - cx) * 0.15));
    const dy = Math.max(-10, Math.min(10, (e.clientY - cy) * 0.15));
    target.style.transform = `translate(${dx}px, ${dy}px)`;
  }, [prefersReduced, isFinePointer]);

  const handleCtaLeave = useCallback((e: React.MouseEvent) => {
    const target = e.currentTarget.firstElementChild as HTMLElement;
    if (target) target.style.transform = "translate(0, 0)";
  }, []);

  const line1Words = footer.heading1.split(" ");
  const line2Words = footer.headingHighlight.split(" ");

  const ctaHref = "#book";

  const ctaProps = { href: ctaHref };

  return (
    <section className="pt-20 md:pt-24 pb-6 bg-background border-t border-border/30 relative overflow-hidden">
      {/* Ambient glow */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/25 blur-[120px] pointer-events-none"
        style={{ mixBlendMode: "screen", willChange: "transform" }}
      />

      <div className="container mx-auto px-6 max-w-5xl text-center relative z-10">
        {/* Headline with word-split reveal */}
        <h2 className={`${isPL ? "text-5xl md:text-7xl lg:text-[7rem]" : "text-6xl md:text-8xl lg:text-9xl"} font-display leading-[1.05] tracking-tight mb-14`}>
          {line1Words.map((word, i) => (
            <motion.span
              key={`fh1-${i}`}
              initial={prefersReduced ? undefined : { opacity: 0, y: 20, filter: "blur(8px)" }}
              whileInView={prefersReduced ? undefined : { opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block mr-[0.1em] pr-[0.08em]"
            >
              {word}
            </motion.span>
          ))}
          <br />
          {line2Words.map((word, i) => (
            <motion.span
              key={`fh2-${i}`}
              initial={prefersReduced ? undefined : { opacity: 0, y: 20, filter: "blur(8px)" }}
              whileInView={prefersReduced ? undefined : { opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (line1Words.length + i) * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block mr-[0.1em] pr-[0.08em] text-gradient"
            >
              {word}
            </motion.span>
          ))}
        </h2>

        {/* Magnetic CTA */}
        <motion.div
          ref={ctaContainerRef}
          initial={prefersReduced ? undefined : { opacity: 0, y: 30 }}
          whileInView={prefersReduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="inline-block"
          onMouseMove={handleCtaMove}
          onMouseLeave={handleCtaLeave}
        >
          <a
            {...ctaProps}
            onClick={() => posthog?.capture("footer_cta_clicked", { variant })}
            className={`inline-flex items-center gap-3 px-12 py-6 rounded-full font-body font-semibold text-base transition-all glow ${
              isAmber
                ? "bg-accent text-accent-foreground hover:bg-accent/90"
                : "bg-primary text-primary-foreground hover:bg-primary/90"
            }`}
            style={{ transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms" }}
          >
            {footer.cta}
            <ArrowRight size={18} />
          </a>
        </motion.div>

      </div>

      <div className="container mx-auto px-6">
        <SiteFooter />
      </div>
    </section>
  );
};

export default FooterCTA;
